import isArray from 'lodash/isArray';
import {
	SELECT_TAB_KEY,
	EXTERNAL_SOURCE_TAB_KEY,
	PREVIOUS_STEP_TABLE_OUTPUT,
	DATA_INPUT_MENU_ALLOWED_VALUES,
} from './helpers/data-input-menu.const';
import { DATA_INPUT_MENU_MIN_WIDTH } from '@/components/DataInput/DataInputMenu/helpers/data-input-menu.const';

// Private
const DATA_INPUT_MESSAGES = {
	ITEM_DATA_ONLY: 'This field only accepts item data.',
	COLUMN_AND_ITEM: 'This field only accepts column or item data.',
	TABLE_ONLY: 'This field only accepts table data. Item data is hidden.',
};

const valueTypesAreTable = (valueTypes) => valueTypes.includes(DATA_INPUT_MENU_ALLOWED_VALUES.TABLE);

// Public
const valueTypesValidator = (valuesTypes) => {
	const ALLOWED_VALUES_LIST = Object.values(DATA_INPUT_MENU_ALLOWED_VALUES);
	// make sure every valueType received as prop is from the DATA_INPUT_MENU_ALLOWED_VALUES
	return valuesTypes.every((valueType) => ALLOWED_VALUES_LIST.includes(valueType));
};

const getDataInputMenuMessageForValueTypes = (valueTypes) => {
	let menuMessage;
	if (valueTypesAreTable(valueTypes)) {
		menuMessage = DATA_INPUT_MESSAGES.TABLE_ONLY;
	} else {
		const columnValueType = valueTypes.includes(DATA_INPUT_MENU_ALLOWED_VALUES.TABLE_COLUMN);
		const itemDataValueType = valueTypes.includes(DATA_INPUT_MENU_ALLOWED_VALUES.ITEM_DATA);
		if (itemDataValueType) {
			menuMessage = columnValueType ? DATA_INPUT_MESSAGES.COLUMN_AND_ITEM : DATA_INPUT_MESSAGES.ITEM_DATA_ONLY;
		}
	}
	return menuMessage;
};

const isValueAccepted = (valuesTypes, value, tabCode) => {
	const isValueAnArray = isArray(value);
	const isTableValidValue = valuesTypes.includes(DATA_INPUT_MENU_ALLOWED_VALUES.TABLE) && isValueAnArray;
	const isColumnValidValue = valuesTypes.includes(DATA_INPUT_MENU_ALLOWED_VALUES.TABLE_COLUMN) && tabCode === PREVIOUS_STEP_TABLE_OUTPUT;
	const isSelectValidValue = [EXTERNAL_SOURCE_TAB_KEY, SELECT_TAB_KEY].includes(tabCode);
	const isItemDataValidValue = valuesTypes.includes(DATA_INPUT_MENU_ALLOWED_VALUES.ITEM_DATA) && !isValueAnArray;
	return isTableValidValue || isColumnValidValue || isSelectValidValue || isItemDataValidValue;
};

const getNounForInvalidValueType = (valuesTypes) => (valueTypesAreTable(valuesTypes) ? 'property' : 'table');

const getDataInputMenuAbsoluteElements = (prefixSelector = '') =>
	Array.from(document.querySelectorAll(`${prefixSelector}.absolute-data-input-menu`));

const getMenuItemUniqueKey = (step = {}, itemKey = '') => `${step.key}_menuItemUniqueKey_${itemKey || step.itemKey}`;

const getCursorPosition = () => {
	const sel = document.getSelection();
	try {
		const r = sel.getRangeAt(0);
		let rect;
		let r2;
		const node = r.startContainer;
		const offset = r.startOffset;
		if (offset > 0) {
			// new range, don't influence DOM state
			r2 = document.createRange();
			r2.setStart(node, offset - 1);
			r2.setEnd(node, offset);
			rect = r2.getBoundingClientRect();
			return { x: rect.right, y: rect.top };
		} else if (offset < node.length) {
			r2 = document.createRange();
			// similar but select next on letter
			r2.setStart(node, offset);
			r2.setEnd(node, offset + 1);
			rect = r2.getBoundingClientRect();
			return { x: rect.left, y: rect.top };
		} else {
			// textNode has length
			rect = node.getBoundingClientRect();
			const styles = getComputedStyle(node);
			const lineHeight = parseInt(styles.lineHeight);
			const fontSize = parseInt(styles.fontSize);
			// roughly half the whitespace... but not exactly
			const delta = (lineHeight - fontSize) / 2;
			return { x: rect.left, y: rect.top + delta };
		}
	} catch {
		return { x: 0, y: 0 };
	}
};

const MENU_HEIGHT = 50;
const CARRET_HEIGHT = 15;
const MENU_WIDTH = DATA_INPUT_MENU_MIN_WIDTH;

const getMenuRectByCarretPosition = (menuHeight, menuWidth, carretCoordinatesOnSlashOpen) => {
	const buildMenuRect = (dir, x, y) => ({ dir, x, y });

	const canFitFromBottomToCarret = carretCoordinatesOnSlashOpen.y + menuHeight < window.innerHeight - 50;
	const canFitFromRightToCarret = carretCoordinatesOnSlashOpen.x + menuWidth < window.innerWidth;
	const menuOverflowOnYAxis = canFitFromBottomToCarret ? 'bottom' : 'top';
	const menuLeft = canFitFromRightToCarret ? carretCoordinatesOnSlashOpen.x : carretCoordinatesOnSlashOpen.x - menuWidth;
	return buildMenuRect(
		menuOverflowOnYAxis,
		menuLeft,
		canFitFromBottomToCarret ? carretCoordinatesOnSlashOpen.y + CARRET_HEIGHT : carretCoordinatesOnSlashOpen.y
	);
};

const getMenuRectByTextBoxPosition = (targetElement, menuHeight, menuWidth) => {
	const buildMenuRect = (dir, x, y) => ({ dir, x, y });
	const rect = targetElement.getBoundingClientRect();
	const overflowFromBottom = rect.bottom + menuHeight > window.innerHeight;
	if (overflowFromBottom) {
		const overflowFromTop = rect.top < menuHeight;
		if (overflowFromTop) {
			const overflowFromRight = rect.right + menuWidth > window.innerWidth;
			if (overflowFromRight) {
				const overflowFromLeft = rect.left < menuWidth;
				if (overflowFromLeft) {
					//console.log('WE DONT HAVE ROOM TO RENDER MENU, WILL BE OVER INPUT');
				}
				return buildMenuRect('left', rect.left, rect.top);
			} else {
				return buildMenuRect('right', rect.right, rect.top);
			}
		} else {
			return buildMenuRect('top', rect.left, rect.top);
		}
	} else {
		return buildMenuRect('bottom', rect.left, rect.bottom);
	}
};

const isBottomToTopLayout = (menuCoordinates) => menuCoordinates?.dir === 'top';

const getMenuRect = (args) => {
	//  menuHeight, menuWidth, in order to open menu from carret position
	const { dataInputMenuElement, carretCoordinatesOnSlashOpen, didOpenBySlash = false } = args;
	let { targetElement, menuWidth = MENU_WIDTH, menuHeight = MENU_HEIGHT } = args;
	if (!args.targetElement) {
		targetElement = args;
	}
	const menuRect = dataInputMenuElement?.getBoundingClientRect();
	menuHeight = menuRect?.height || MENU_HEIGHT;
	menuWidth = menuRect?.width || MENU_WIDTH;
	if (didOpenBySlash && carretCoordinatesOnSlashOpen?.x) {
		const menuCoordinates = getMenuRectByCarretPosition(menuHeight, menuWidth, carretCoordinatesOnSlashOpen);
		return { ...menuCoordinates, isBottomToTopLayout: isBottomToTopLayout(menuCoordinates), didOpenBySlash };
	} else {
		const menuCoordinates = getMenuRectByTextBoxPosition(targetElement, menuHeight, menuWidth);
		return {
			...menuCoordinates,
			isBottomToTopLayout: isBottomToTopLayout(menuCoordinates),
			didOpenBySlash,
		};
	}
};

const DataInputMenuService = {
	valueTypesValidator,
	getDataInputMenuMessageForValueTypes,
	isValueAccepted,
	getNounForInvalidValueType,
	getDataInputMenuAbsoluteElements,
	getMenuRect,
	getMenuItemUniqueKey,
	getCursorPosition,
	valueTypesAreTable,
};

export default DataInputMenuService;
